import {
  colors,
  dimensions,
  Knob,
  StyledFC
} from '@bettermarks/bm-ui-components';
import hexToRgba from 'hex-to-rgba';
import React, {useCallback, useState} from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps
} from 'react-router-dom';
import styled from 'styled-components';

import {
  LibraryTileProps,
  ModuleDetailProps,
  ModuleProps,
  TileProps
} from './types';

import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  LinkOverlayProps,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
  useDisclosure,
  useColorMode,
  useTheme,
  chakra,
  ChakraComponent,
  BoxProps,
  LinkBoxProps
} from '@chakra-ui/react';

import {BsHeart, BsHeartFill} from 'react-icons/bs/index';
import {ActionTip, ActionTipProps} from '../ActionTip';
import {DetailTile} from '../DetailTile';
import {Card} from '../Card';

type LinkButtonProps = LinkOverlayProps & RouterLinkProps;

const LinkButton: React.FC<LinkButtonProps> = React.forwardRef(
  (props: LinkButtonProps, ref: React.Ref<any>) => {
    return <LinkOverlay ref={ref} as={RouterLink} {...props} />;
  }
);

const ModuleTileBase = styled.a<Pick<TileProps, 'maxTileWidth'>>`
  display: flex;
  max-width: ${p => p.maxTileWidth || 'unset'};
  position: relative;
`;

const ModuleImage = styled.span`
  img {
    max-width: 100%;
    cursor: pointer;
  }
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${p => p.color && hexToRgba(p.color, 0.2)};
`;

const ActionButton = styled(Knob)`
  display: flex;
  padding: ${dimensions.spaceXs};
  border-radius: ${dimensions.borderRadiusM};
  &:hover {
    background-color: ${colors.cWhite};
  }
`;

const ActionButtons = styled.div`
  display: flex;
`;

const ModuleDetails = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${dimensions.spaceXs};
`;

const ModuleTitle = styled.h3`
  font-size: ${dimensions.fontSizeM};
  font-weight: normal;
  line-height: ${dimensions.lineHeightM2};
  color: ${colors.cGray800};
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  word-break: break-word;
`;

const PopOverAddToPlan: React.FC<{
  dialogue: string;
  isPlanned?: boolean;
  // onClose: any;
  // onOpen: any;
  // isOpen?: boolean;
  actionButton?: any;
}> = ({dialogue, isPlanned, actionButton}) => {
  //const initRef = React.useRef();
  const {isOpen, onToggle, onClose} = useDisclosure();

  return (
    <Popover
      returnFocusOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      closeOnBlur={true}
      placement='auto'
    >
      <PopoverTrigger>
        <Button
          aria-label='Favorit Button'
          size='sm'
          flexShrink={0}
          variant='ghost'
          sx={{
            '& > svg': {width: '18px', height: '18px'},
            '& .saved': {fill: 'error.300'}
          }}
          onClick={onToggle}
        >
          {isPlanned ? (
            <BsHeartFill className='saved' />
          ) : (
            <BsHeart className='unsaved' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <p>{dialogue}</p>
          {actionButton}
          <Button
            aria-label='Abbrechen Button'
            size='sm'
            variant='ghost'
            onClick={onClose}
          >
            Abbrechen
          </Button>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

interface PreviewModalProps {
  title: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({title}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  return (
    <>
      <Button onClick={onOpen}>Open Modal</Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <p>test</p>
          </ModalBody>

          <ModalFooter>
            <Button
              aria-label='Schließen Button'
              colorScheme='blue'
              mr={3}
              onClick={onClose}
            >
              Close
            </Button>
            <Button variant='ghost'>Secondary Action</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const BasicModal: React.FC = ({}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  return (
    <>
      <Button onClick={onOpen}>Open Modal</Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modal Title</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <p>blabla</p>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={onClose}>
              Close
            </Button>
            <Button variant='ghost'>Secondary Action</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const ModuleTile: StyledFC<ModuleProps & ActionTipProps> = ({
  id,
  modalTitle,
  title,
  image,
  color,
  onOpenModuleDetails,
  onOpenModule,
  dialogue,
  closeLabel,
  actionButton,
  onToggleActionTip,
  isPlanned,
  customPreviewIcon,
  customAddToPlaningIcon,
  url,
  onOpenPlanning,
  onAddToPlanning,
  buttonLayout,
  tipPosition
}) => {
  const [isActive, setActive] = React.useState(false);
  const [modalDetailProps, setModuleDetailProps] =
    React.useState<ModuleDetailProps>({
      objectives: [],
      preknowledge: [],
      examples: []
    });

  const setModalDetailsAndOpen = (modalDetails: ModuleDetailProps) => {
    setModuleDetailProps(modalDetails);
    setActive(true);
    return () => setActive(false);
  };

  const onModalToggled = (id: string) =>
    onOpenModuleDetails(id, setModalDetailsAndOpen);

  const stopProp = useCallback((e: Event | React.MouseEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }, []);

  const [tipVisibility, setTipVisibilityTo] = useState(false);

  const {objectives, preknowledge, examples} = modalDetailProps;

  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal
  } = useDisclosure();

  return (
    <Card>
      <Flex alignItems='flex-start' gap='16px'>
        <ModuleImage
          onClick={e => {
            onOpenModule(id);
            return stopProp(e);
          }}
          color={color}
        >
          {image && <img src={image} alt={title} />}
        </ModuleImage>
        <Flex flex={1} justifyContent='space-between' alignItems='flex-start'>
          <Heading
            as='h2'
            variant='h2'
            onClick={e => {
              onOpenModule(id);
              return stopProp(e);
            }}
          >
            {title}
          </Heading>
          <PopOverAddToPlan
            isPlanned={isPlanned}
            dialogue={dialogue}
            actionButton={actionButton}
          />
        </Flex>
      </Flex>
      <Flex justifyContent='flex-end' gap='8px'>
        <Button
          onClick={(e: Event | React.MouseEvent<any, MouseEvent>) => {
            onModalToggled(id);
            return stopProp(e);
          }}
          variant='ghost'
          size='sm'
        >
          Vorschau
        </Button>
        <Button
          onClick={e => {
            onOpenModule(id);
            return stopProp(e);
          }}
          variant='solid'
          colorScheme='blue'
          size='sm'
        >
          Öffnen
        </Button>

        {/* <PreviewModal title={title} /> */}
      </Flex>

      <DetailTile
        id={id}
        color={color}
        modalTitle={modalTitle}
        title={title}
        image={image}
        objectives={objectives}
        preknowledge={preknowledge}
        examples={examples}
        isActive={isActive}
        isPlanned={isPlanned}
        onClose={(e: Event) => {
          onModalToggled(id);
          return stopProp(e);
        }}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => stopProp(e)}
        onAddToPlanning={onAddToPlanning}
        onOpenPlanning={onOpenPlanning}
        onOpenModule={() => onOpenModule(id)}
        context='modal'
      />
    </Card>
  );
};

const _ModuleTile = styled(ModuleTileBase)`
  cursor: pointer;
  position: relative;
  text-decoration: none;
  box-shadow: inset 0 1px 0 ${colors.cGray400};
  padding: ${dimensions.spaceS} ${dimensions.spaceXs};
  gap: ${dimensions.spaceM};
  transition: background-color 0.25s ease-in-out;
  &:hover {
    background-color: ${colors.cGray100};
  }
  ${ActionTip} {
    top: calc(100% - 20px);
  }
`;

const TileRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  flex: 1;
`;

const IconBox: React.FC<{
  grades?: number[];
  icon?: any;
  iconPadding?: any;
  iconWidth?: number;
  favorite?: any;
}> = ({grades, icon, favorite}) => {
  return (
    <>
      {grades ? (
        <Box
          color={useColorModeValue('gray.500', 'gray.200')}
          width='65px'
          height='65px'
          flex='0 0 65px'
          borderRadius='40px'
          display='flex'
          justifyContent='center'
          alignItems='center'
          border='1px solid'
          fontSize={
            grades.length <= 1 ? '34px' : grades.length === 2 ? '24px' : '20px'
          }
          fontWeight='bold'
          borderColor={useColorModeValue('gray.500', 'gray.200')}
        >
          {grades.length > 1 ? grades.toString().replace(/,/g, '/') : grades}
        </Box>
      ) : (
        <Box>{icon}</Box>
      )}
      {favorite}
    </>
  );
};

export const LibraryTile: React.FC<LibraryTileProps> = ({
  icon,
  url,
  grades,
  tooltip,
  isActive,
  iconPadding,
  title,
  themeCount,
  classRange,
  beta,
  favorite
}) => {
  return (
    <LinkBox
      as='figure'
      aria-label='Produkt Kachel'
      padding='16px'
      borderRadius='8px'
      alignItems='flex-start'
      transition='box-shadow 0.35s ease-in-out 0s'
      _hover={{
        boxShadow: `${useColorModeValue(
          'rgb(230 230 230)',
          'rgb(0 0 0)'
        )} 0px 6px 14px`
      }}
      bg={
        isActive
          ? useColorModeValue('bmPrimaryAlpha.200', 'bmPrimaryAlpha.600')
          : useColorModeValue('white', 'blackAlpha.300')
      }
      border='1px solid'
      borderColor={
        isActive
          ? useColorModeValue('bmPrimary.500', 'bmPrimary.800')
          : useColorModeValue('gray.200', 'whiteAlpha.500')
      }
      flexDir='column'
    >
      <Flex gap='16px' width='100%' alignItems='center'>
        {icon && (
          <IconBox
            grades={grades}
            icon={icon}
            iconPadding={iconPadding}
            favorite={favorite}
          />
        )}

        <Flex
          flexDirection='column'
          flex='1 1 auto'
          justifyContent='center'
          gap='4px'
          flexWrap='wrap'
        >
          <Heading as='h3' size='md' wordBreak='break-word'>
            <LinkButton aria-label='Themen Titel' title={tooltip} to={url}>
              {title}
            </LinkButton>
          </Heading>
          <Flex align='center' gap='8px' flexWrap='wrap'>
            {beta && <Badge colorScheme='purple'>beta</Badge>}
            {themeCount && (
              <Text>
                {themeCount + ' ' + (themeCount > 1 ? 'Inhalte' : 'Inhalt')}
              </Text>
            )}
            {classRange && <Text>{classRange}</Text>}
          </Flex>
        </Flex>
      </Flex>
    </LinkBox>
  );
};
